import { useEffect, useMemo, useRef, useState } from "react"

import { Stack, useMediaQuery } from "@mui/material"
import theme from "@skistarab/mui-theme"
import { useTranslation } from "react-i18next"
import { useLocation, useSearch } from "wouter"

import {
  DateSelector,
  ResortSelector,
  ProductSearchInputField,
} from "@/components"
import { DATE_SEARCH_PARAM_NAME } from "@/config"
import { useDataLayer } from "@/features/DataLayer"
import { useDateConstraints } from "@/hooks"
import useDateLocale from "@/hooks/useDateLocale"
import { useDateParam, useResortParam } from "@/hooks/useSkipassFlow"
import useCartContextQuery from "@/queries/hooks/useCartContextQuery"
import { Resort } from "@/queries/models"
import { useSkipassStore } from "@/store/skipassStore"
import { formatDateForUrl, formatStartDate } from "@/utils/dateUtils"
import {
  DEFAULT_RESORT_SLUG,
  getSlugFromExternalId,
} from "@/utils/resortSlugMap"

import { usePassSearchEditEvent } from "../DataLayer/hooks"

type PassSearchProps = {
  resorts?: Resort[] | undefined
  isResortsLoading: boolean
}

const PassSearch = ({ resorts, isResortsLoading }: PassSearchProps) => {
  const { t } = useTranslation()
  const locale = useDateLocale()
  const search = useSearch()
  const [, navigate] = useLocation()
  const { resortExternalId } = useResortParam()
  const { startDate, setStartDate } = useDateParam()
  const pushPassSearchEvent = useDataLayer("PassSearch", { sendOnce: true })
  const trackSearchEditEvent = usePassSearchEditEvent()
  const resortSelectorRef = useRef<HTMLDivElement>(null)

  // Local state for temporary selections
  const [localResortExternalId, setLocalResortExternalId] =
    useState(resortExternalId)
  const [localStartDate, setLocalStartDate] = useState(startDate)

  const isTablet = useMediaQuery(theme.breakpoints.up("md"))
  const { data: cartContext, isLoading: isCartContextLoading } =
    useCartContextQuery(localResortExternalId)
  const {
    minDate,
    maxDate,
    unAvailableDates,
    isLoading: isDateConstraintsLoading,
  } = useDateConstraints(localResortExternalId)
  const showResortSkeleton = resorts === undefined && isResortsLoading

  const hasArrivalDateBeenSet = useSkipassStore((s) => s.hasArrivalDateBeenSet)
  const setArrivalDateHasBeenSet = useSkipassStore(
    (s) => s.setArrivalDateHasBeenSet
  )

  // Sync local state with store state when store changes
  useEffect(() => {
    setLocalResortExternalId(resortExternalId)
  }, [resortExternalId])

  useEffect(() => {
    setLocalStartDate(startDate)
  }, [startDate])

  useEffect(() => {
    if (!cartContext?.arrival) return
    if (hasArrivalDateBeenSet) return

    setLocalStartDate(cartContext.arrival)
    setStartDate(cartContext.arrival)
    setArrivalDateHasBeenSet()
  }, [
    cartContext?.arrival,
    setStartDate,
    hasArrivalDateBeenSet,
    setArrivalDateHasBeenSet,
  ])

  const formattedDate = useMemo(() => {
    return formatStartDate(localStartDate, locale)
  }, [localStartDate, locale])

  const selectedResort = useMemo(() => {
    return resorts?.find(
      (resort) => resort.externalId === localResortExternalId
    )
  }, [localResortExternalId, resorts])

  useEffect(() => {
    pushPassSearchEvent()
  }, [pushPassSearchEvent])

  const handleSearch = () => {
    const slug =
      getSlugFromExternalId(localResortExternalId) ?? DEFAULT_RESORT_SLUG
    const params = new URLSearchParams(search)
    params.set(DATE_SEARCH_PARAM_NAME, formatDateForUrl(localStartDate))
    navigate(`/${slug}/?${params.toString()}`, { replace: true })
    trackSearchEditEvent(localResortExternalId, localStartDate)
  }

  return (
    <ProductSearchInputField
      title={selectedResort?.name}
      titleLoading={isResortsLoading}
      items={[formattedDate]}
      itemsLoading={isCartContextLoading}
      searchButtonText={t("searchPassButtonText")}
      focusRef={resortSelectorRef}
      onSearch={handleSearch}
    >
      <Stack
        spacing={isTablet ? 1.5 : 1}
        direction={isTablet ? "row" : "column"}
      >
        <ResortSelector
          ref={resortSelectorRef}
          selectedResortExternalId={localResortExternalId}
          resorts={resorts}
          setSelectedResort={setLocalResortExternalId}
          isLoading={showResortSkeleton}
        />
        <DateSelector
          label={t("dateSelectLabel")}
          selectedDate={localStartDate}
          setSelectedDate={setLocalStartDate}
          minDate={minDate}
          maxDate={maxDate}
          unAvailableDates={unAvailableDates}
          isLoadingDates={isDateConstraintsLoading || isCartContextLoading}
        />
      </Stack>
    </ProductSearchInputField>
  )
}

export default PassSearch
